import { anzeigeEinzelpreisCent, kgMengenText } from '../../../packages/rksv-kern/src/mengen';

import { istRabatt } from './rabatt';
import { formatiereEuro } from './warenkorb';
import type { Position } from './warenkorb';

/**
 * Kundenanzeige: ein zweites Browserfenster (kundenseitiger Bildschirm, z. B.
 * Posiflex LM-3010E mit 1024 × 768 am VGA-Ausgang) zeigt den laufenden Bon, den
 * Zahlungsstand und nach dem Kassieren Gezahlt/Rückgeld. Die Kasse sendet
 * Nachrichten über einen BroadcastChannel (gleiche Origin, gleiches Gerät, gleiches
 * Browserprofil); die Anzeige hält daraus ihren Stand. Reine Logik hier,
 * Darstellung in der Komponente. Beträge in Cent (Regel 5).
 *
 * Trennung zweier Kassen: Kanal je Kassen-ID, zusätzlich trägt jede Nachricht die
 * Kassen-ID und die Anzeige verwirft fremde. Die Anzeige ist über `?kasse=` fest an
 * eine Kasse gebunden und zeigt nichts, wenn das Gerät mit einer anderen verbunden ist.
 *
 * Mehrere Verkaufsfenster derselben Kasse: Die Anzeige folgt dem Fenster, dessen Bon
 * zuletzt geändert wurde. Antworten auf eine Standabfrage übernehmen eine bestehende
 * Bindung nur mit jüngerem Änderungszeitpunkt.
 */
export const KUNDENANZEIGE_KANAL = 'bilanx.kundenanzeige';

/** Wie lange „Vielen Dank“ stehen bleibt, bevor die Anzeige zum Willkommen zurückkehrt. */
export const DANKE_MS = 8_000;
/** Abstand der Lebenszeichen des Kassenfensters. */
export const PULS_MS = 5_000;
/**
 * Ohne Lebenszeichen des Kassenfensters so lange (abgestürzt, geschlossen): zurück
 * zum Willkommen. Großzügig, weil Chrome Zeitgeber verdeckter Fenster auf einmal je
 * Minute drosseln kann.
 */
export const VERWAIST_MS = 90_000;

/** Kanal je Kasse: zwei Kassen im selben Browser stören sich nicht. */
export function kundenanzeigeKanal(kassenId: string): string {
  return `${KUNDENANZEIGE_KANAL}.${kassenId}`;
}

/**
 * Zahlungsstand während des Kassierens, aus dem Zustand des Verkaufs abgeleitet:
 * `keine` (nichts offen), `bar` (Bar-Dialog), `karte` (am Terminal), `beleg` (bezahlt,
 * Beleg wird erstellt), `pruefung` (Ergebnis oder Beleg ungeklärt), `abgelehnt`,
 * `abgebrochen` (Kartenzahlung ohne Erfolg).
 */
export type Zahlungsanzeige =
  'keine' | 'bar' | 'karte' | 'beleg' | 'pruefung' | 'abgelehnt' | 'abgebrochen';

const ZAHLUNGSANZEIGEN: readonly Zahlungsanzeige[] = [
  'keine',
  'bar',
  'karte',
  'beleg',
  'pruefung',
  'abgelehnt',
  'abgebrochen',
];

export type Zahlungsart = 'bar' | 'karte' | 'gutschein';

export type KundenanzeigeNachricht = (
  | {
      typ: 'bon';
      positionen: Position[];
      /** Offener Betrag nach Gutscheinen. */
      summeCent: number;
      gutscheinCent: number;
      /** Zeitpunkt der letzten Änderung dieses Bons im sendenden Fenster (ms). */
      geaendertUm?: number;
      /** Antwort auf eine Standabfrage (kein Bedienschritt). */
      antwort?: boolean;
    }
  | { typ: 'zahlung'; status: Zahlungsanzeige; betragCent: number }
  | {
      typ: 'bezahlt';
      /** Belegsumme (einschließlich Gutscheinanteil). */
      summeCent: number;
      /** Mit Gutschein bezahlter Teil der Belegsumme. */
      gutscheinCent?: number;
      zahlungsart: Zahlungsart;
      gegebenCent: number | null;
      rueckgeldCent: number | null;
      trinkgeldCent: number;
    }
  | { typ: 'leer' }
  /** Kassenfenster schließt bzw. verlässt den Verkauf. */
  | { typ: 'ende' }
  /** Lebenszeichen des Kassenfensters. */
  | { typ: 'puls' }
  /** Anzeige bittet die Kassenfenster um ihren aktuellen Stand. */
  | { typ: 'anfrage' }
) & {
  /** Sendendes Kassenfenster: die Anzeige folgt dem Fenster, dessen Bon sie gerade zeigt. */
  quelle?: string;
  /** Kasse des Senders; eine Anzeige verwirft Nachrichten anderer Kassen. */
  kassenId?: string;
};

export interface KundenanzeigeStand {
  modus: 'willkommen' | 'bon' | 'bezahlt';
  positionen: Position[];
  summeCent: number;
  gutscheinCent: number;
  /** Zahlungsstand zum gezeigten Bon (null: nichts offen). */
  zahlung: { status: Exclude<Zahlungsanzeige, 'keine'>; betragCent: number } | null;
  bezahlt: {
    summeCent: number;
    gutscheinCent: number;
    zahlungsart: Zahlungsart;
    gegebenCent: number | null;
    rueckgeldCent: number | null;
    trinkgeldCent: number;
  } | null;
}

export const LEERER_STAND: KundenanzeigeStand = {
  modus: 'willkommen',
  positionen: [],
  summeCent: 0,
  gutscheinCent: 0,
  zahlung: null,
  bezahlt: null,
};

function gleicherBon(
  stand: KundenanzeigeStand,
  positionen: Position[],
  summeCent: number,
): boolean {
  return (
    stand.modus === 'bon' &&
    stand.summeCent === summeCent &&
    JSON.stringify(stand.positionen) === JSON.stringify(positionen)
  );
}

/** Nächster Anzeigestand aus einer Nachricht; „leer“ oder ein leerer Bon führt zurück zum Willkommen. */
export function naechsterStand(
  stand: KundenanzeigeStand,
  n: KundenanzeigeNachricht,
): KundenanzeigeStand {
  switch (n.typ) {
    case 'bon':
      return n.positionen.length === 0
        ? LEERER_STAND
        : {
            modus: 'bon',
            positionen: n.positionen,
            summeCent: n.summeCent,
            gutscheinCent: n.gutscheinCent,
            // Der Zahlungsstand bleibt nur, solange sich der Bon nicht ändert.
            zahlung: gleicherBon(stand, n.positionen, n.summeCent) ? stand.zahlung : null,
            bezahlt: null,
          };
    case 'zahlung':
      if (stand.modus !== 'bon') {
        return stand;
      }
      return {
        ...stand,
        zahlung: n.status === 'keine' ? null : { status: n.status, betragCent: n.betragCent },
      };
    case 'bezahlt':
      return {
        ...stand,
        modus: 'bezahlt',
        zahlung: null,
        bezahlt: {
          summeCent: n.summeCent,
          gutscheinCent: n.gutscheinCent ?? 0,
          zahlungsart: n.zahlungsart,
          gegebenCent: n.gegebenCent,
          rueckgeldCent: n.rueckgeldCent,
          trinkgeldCent: n.trinkgeldCent,
        },
      };
    case 'leer':
    case 'ende':
      return LEERER_STAND;
    case 'puls':
    case 'anfrage':
      return stand;
  }
}

function istCent(x: unknown): x is number {
  return typeof x === 'number' && Number.isSafeInteger(x);
}

function istPosition(x: unknown): boolean {
  if (typeof x !== 'object' || x === null) {
    return false;
  }
  const p = x as Record<string, unknown>;
  const nachlass = p.zeilenNachlass as Record<string, unknown> | undefined | null;
  return (
    typeof p.bezeichnung === 'string' &&
    p.bezeichnung.length <= 200 &&
    typeof p.menge === 'number' &&
    Number.isFinite(p.menge) &&
    istCent(p.bruttoCent) &&
    typeof p.steuersatz === 'string' &&
    (p.einheit === undefined || p.einheit === 'kg') &&
    (p.einzelpreisCent === undefined || istCent(p.einzelpreisCent)) &&
    (nachlass === undefined ||
      (typeof nachlass === 'object' &&
        nachlass !== null &&
        typeof nachlass.basisCent === 'number' &&
        Number.isFinite(nachlass.basisCent) &&
        typeof nachlass.prozent === 'number' &&
        Number.isFinite(nachlass.prozent)))
  );
}

/** Vollständige Prüfung: eine fremde oder kaputte Nachricht darf die Anzeige nicht abstürzen lassen. */
export function istKundenanzeigeNachricht(x: unknown): x is KundenanzeigeNachricht {
  if (typeof x !== 'object' || x === null) {
    return false;
  }
  const n = x as Record<string, unknown>;
  if (n.quelle !== undefined && typeof n.quelle !== 'string') {
    return false;
  }
  if (n.kassenId !== undefined && typeof n.kassenId !== 'string') {
    return false;
  }
  switch (n.typ) {
    case 'bon':
      return (
        Array.isArray(n.positionen) &&
        n.positionen.length <= 500 &&
        n.positionen.every(istPosition) &&
        istCent(n.summeCent) &&
        istCent(n.gutscheinCent) &&
        (n.geaendertUm === undefined || istCent(n.geaendertUm)) &&
        (n.antwort === undefined || typeof n.antwort === 'boolean')
      );
    case 'zahlung':
      return ZAHLUNGSANZEIGEN.includes(n.status as Zahlungsanzeige) && istCent(n.betragCent);
    case 'bezahlt':
      return (
        istCent(n.summeCent) &&
        (n.gutscheinCent === undefined || istCent(n.gutscheinCent)) &&
        (n.zahlungsart === 'bar' || n.zahlungsart === 'karte' || n.zahlungsart === 'gutschein') &&
        (n.gegebenCent === null || istCent(n.gegebenCent)) &&
        (n.rueckgeldCent === null || istCent(n.rueckgeldCent)) &&
        istCent(n.trinkgeldCent)
      );
    case 'leer':
    case 'ende':
    case 'puls':
    case 'anfrage':
      return true;
    default:
      return false;
  }
}

/**
 * Gehört die Nachricht zur gerade gezeigten Kassensitzung? Ein neuer Bon (mit
 * Positionen) übernimmt die Anzeige; alles andere zählt nur vom selben Fenster.
 */
export function vonAktiverQuelle(n: KundenanzeigeNachricht, aktiv: string | null): boolean {
  if (aktiv === null || n.quelle === undefined) {
    return true;
  }
  if (n.typ === 'bon' && n.positionen.length > 0) {
    return true;
  }
  return n.quelle === aktiv;
}

/** Eine Bonzeile, wie sie der Kunde sieht. */
export interface AnzeigeZeile {
  bezeichnung: string;
  /** „2 × 1,50 €“ bzw. „0,350 kg …“: nur bei Menge ≠ 1 oder Zeilenrabatt. */
  menge: number;
  /** Einzelpreis vor einem Zeilenrabatt (Cent, gerundet); bei Wiegeartikeln der Kilopreis. */
  einzelCent: number;
  /** Wiegeposition (Menge in kg): Detail immer zeigen, auch bei 1,000 kg. */
  kg: boolean;
  /** Detailtext „2 × 1,50 €“ bzw. „0,750 kg × 5,99 €/kg“ (ohne Rabattangabe). */
  mengeText: string;
  /** Zeilenrabatt in Prozent, sonst null. */
  rabattProzent: number | null;
  bruttoCent: number;
  /** Bonrabatt-Zeile („Rabatt 10 %“). */
  istRabatt: boolean;
}

export function anzeigeZeile(p: Position): AnzeigeZeile {
  const n = p.zeilenNachlass;
  // Wiegeartikel: Kilopreis aus der Position, nie aus dem gerundeten Zeilenbetrag
  // zurückgerechnet (0,125 kg × 5,99 €/kg = 0,75 € ergäbe sonst „6,00 €/kg“).
  const kgText = kgMengenText(p);
  const einzelCent =
    kgText !== null && p.einzelpreisCent !== undefined
      ? p.einzelpreisCent
      : n !== undefined
        ? Math.round(n.basisCent)
        : p.menge === 0
          ? 0
          : anzeigeEinzelpreisCent(p);
  return {
    bezeichnung: p.bezeichnung,
    menge: p.menge,
    einzelCent,
    kg: kgText !== null,
    mengeText: kgText ?? `${formatiereMenge(p.menge)} × ${formatiereEuro(einzelCent)} €`,
    rabattProzent: n !== undefined && n.prozent > 0 ? n.prozent : null,
    bruttoCent: p.bruttoCent,
    istRabatt: istRabatt(p),
  };
}

/**
 * Sichtbare Zeilen bei begrenzter Höhe (1024 × 768): die neuesten Zeilen bleiben
 * sichtbar, ältere werden als „n weitere“ zusammengefasst.
 */
export function sichtbareZeilen(
  positionen: readonly Position[],
  maxZeilen: number,
): { zeilen: AnzeigeZeile[]; ausgeblendet: number } {
  const max = Math.max(2, Math.floor(maxZeilen));
  if (positionen.length <= max) {
    return { zeilen: positionen.map(anzeigeZeile), ausgeblendet: 0 };
  }
  // Eine Zeile geht an den Hinweis „n weitere Positionen“.
  const zeigen = max - 1;
  return {
    zeilen: positionen.slice(positionen.length - zeigen).map(anzeigeZeile),
    ausgeblendet: positionen.length - zeigen,
  };
}

/** Summe aller Nachlässe des Bons in Cent (positiv): Bonrabatt-Zeilen und Zeilenrabatte. */
export function ersparnisCent(positionen: readonly Position[]): number {
  let summe = 0;
  for (const p of positionen) {
    if (istRabatt(p)) {
      summe -= p.bruttoCent;
    } else if (p.zeilenNachlass !== undefined) {
      summe += Math.max(0, Math.round(p.zeilenNachlass.basisCent * p.menge) - p.bruttoCent);
    }
  }
  return summe;
}

/** Menge deutsch: 2 → "2", 0.35 → "0,35". */
export function formatiereMenge(menge: number): string {
  return String(Math.round(menge * 1000) / 1000).replace('.', ',');
}

/**
 * Empfangsseite der Anzeige: Kassen-ID-Prüfung, Fensterbindung, Danke-Zeit und
 * Zurücksetzen bei verwaistem Kassenfenster. Die Zeit wird übergeben (testbar).
 */
export class KundenanzeigeEmpfaenger {
  stand: KundenanzeigeStand = LEERER_STAND;
  /** Kassenfenster, dessen Bon gerade angezeigt wird. */
  private aktiveQuelle: string | null = null;
  /** Änderungszeitpunkt des angezeigten Bons im Kassenfenster. */
  private standZeit = 0;
  private letzterKontakt = 0;
  private dankeBis = 0;
  /** Stand unbekannt (neu geöffnet oder Kassenfenster verwaist): lebende Fenster fragen. */
  private anfrageOffen = true;
  /** Alle Fenster, von denen schon eine Nachricht kam. */
  private readonly bekannt = new Set<string>();
  /**
   * Nach einer Verwaisung sind alle bekannten Fenster gesperrt, bis sie ein neues
   * Lebenszeichen schicken: Der Kanal liefert je Fenster der Reihe nach, also ist alles
   * nach diesem Lebenszeichen aktuell, davor Eingereihtes (Fenster war eingefroren)
   * nicht. Die Sperre gilt je Fenster, auch nach der Wiederbindung an ein anderes.
   */
  private readonly gesperrt = new Set<string>();

  constructor(private readonly kassenId: string) {}

  /**
   * Nachricht verarbeiten. Ergebnis `anfragen`: die Anzeige soll die Kassenfenster
   * um ihren Stand bitten (ein Kassenfenster lebt, der Stand ist aber unbekannt).
   */
  nachricht(roh: unknown, jetzt: number): { geaendert: boolean; anfragen: boolean } {
    const vorher = this.stand;
    // Abgelaufenes zuerst: nach einer Unterbrechung darf keine Nachricht einen
    // verwaisten Bon am Leben halten.
    this.tick(jetzt);
    const ergebnis = (anfragen = false) => ({ geaendert: this.stand !== vorher, anfragen });
    if (!istKundenanzeigeNachricht(roh)) {
      return ergebnis();
    }
    const n = roh;
    if ((n.kassenId !== undefined && n.kassenId !== this.kassenId) || n.typ === 'anfrage') {
      return ergebnis();
    }
    if (n.quelle !== undefined) {
      if (n.typ === 'puls') {
        this.gesperrt.delete(n.quelle);
      } else if (this.gesperrt.has(n.quelle)) {
        return ergebnis();
      }
      this.bekannt.add(n.quelle);
    }
    if (n.typ === 'puls') {
      if (this.aktiveQuelle !== null && n.quelle === this.aktiveQuelle) {
        this.letzterKontakt = jetzt;
      }
      return ergebnis(this.anfrageOffen && this.aktiveQuelle === null);
    }
    if (n.typ === 'bon' && n.positionen.length > 0) {
      const quelle = n.quelle ?? null;
      const zeit = n.geaendertUm ?? jetzt;
      // Während des Danke stellt nur ein Bedienschritt den nächsten Bon dar, keine Antwort.
      if (n.antwort === true && jetzt < this.dankeBis) {
        return ergebnis();
      }
      // Eine Antwort (kein Bedienschritt) verdrängt einen gebundenen Bon nur, wenn ihr
      // Bon jünger ist. Ein Bedienschritt (neuer Artikel) übernimmt immer.
      if (
        n.antwort === true &&
        this.aktiveQuelle !== null &&
        quelle !== this.aktiveQuelle &&
        zeit <= this.standZeit
      ) {
        return ergebnis();
      }
      const fremd = quelle !== this.aktiveQuelle;
      this.aktiveQuelle = quelle;
      this.standZeit = zeit;
      this.letzterKontakt = jetzt;
      this.anfrageOffen = false;
      this.dankeBis = 0;
      // Fensterwechsel: kein Zahlungsstand des anderen Fensters übernehmen.
      this.stand = naechsterStand(fremd ? LEERER_STAND : this.stand, n);
      return ergebnis();
    }
    if (!vonAktiverQuelle(n, this.aktiveQuelle)) {
      return ergebnis();
    }
    // Zahlungsstand und Abschluss gehören immer zu einem gebundenen Bon.
    if ((n.typ === 'zahlung' || n.typ === 'bezahlt') && this.aktiveQuelle === null) {
      return ergebnis();
    }
    this.letzterKontakt = jetzt;
    const leer = n.typ === 'leer' || n.typ === 'ende' || n.typ === 'bon';
    if (leer) {
      // Bon geleert oder Fenster weg: Bindung lösen; das Danke bleibt kurz stehen.
      this.aktiveQuelle = null;
      if (jetzt < this.dankeBis) {
        return ergebnis();
      }
    }
    if (n.typ === 'bezahlt') {
      this.dankeBis = jetzt + DANKE_MS;
    }
    this.stand = naechsterStand(this.stand, n);
    return ergebnis();
  }

  /** Regelmäßig aufrufen: Danke-Zeit abgelaufen oder Kassenfenster verwaist → Willkommen. */
  tick(jetzt: number): boolean {
    if (this.stand.modus === 'bezahlt' && jetzt >= this.dankeBis) {
      this.stand = LEERER_STAND;
      this.aktiveQuelle = null;
      return true;
    }
    if (
      this.stand.modus !== 'willkommen' &&
      this.aktiveQuelle !== null &&
      jetzt - this.letzterKontakt > VERWAIST_MS
    ) {
      this.stand = LEERER_STAND;
      this.aktiveQuelle = null;
      this.anfrageOffen = true;
      for (const q of this.bekannt) {
        this.gesperrt.add(q);
      }
      return true;
    }
    return false;
  }
}

interface Kanal {
  postMessage(n: unknown): void;
  close(): void;
  onmessage: ((e: MessageEvent) => void) | null;
}

export interface SenderUmgebung {
  BroadcastChannel?: typeof BroadcastChannel;
  setInterval?: (rueckruf: () => void, ms: number) => unknown;
  clearInterval?: (handle: unknown) => void;
  /** Uhr (ms) für den Änderungszeitpunkt; Standard `Date.now`. */
  jetzt?: () => number;
}

/** Sender der Kasse; ohne BroadcastChannel (alte Browser) wird nichts gesendet. */
export class KundenanzeigeSender {
  private readonly kanal: Kanal | null;
  private readonly quelle = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  /** Letzter Bon und Zahlungsstand – Antwort auf die Anfrage einer Anzeige. */
  private letzterBon: Extract<KundenanzeigeNachricht, { typ: 'bon' }> | null = null;
  private letzteZahlung: KundenanzeigeNachricht | null = null;
  private puls: unknown = null;
  private readonly umgebung: SenderUmgebung;

  constructor(
    private readonly kassenId: string,
    umgebung: SenderUmgebung = globalThis as SenderUmgebung,
  ) {
    this.umgebung = umgebung;
    this.kanal =
      typeof umgebung.BroadcastChannel === 'function'
        ? new umgebung.BroadcastChannel(kundenanzeigeKanal(kassenId))
        : null;
    if (this.kanal !== null) {
      this.kanal.onmessage = (e: MessageEvent) => {
        const n: unknown = e.data;
        if (
          istKundenanzeigeNachricht(n) &&
          n.typ === 'anfrage' &&
          (n.kassenId === undefined || n.kassenId === this.kassenId)
        ) {
          this.antworte();
        }
      };
      if (typeof umgebung.setInterval === 'function') {
        this.puls = umgebung.setInterval(() => {
          this.post({ typ: 'puls' });
        }, PULS_MS);
      }
    }
  }

  private jetzt(): number {
    return this.umgebung.jetzt?.() ?? Date.now();
  }

  sende(n: KundenanzeigeNachricht): void {
    if (n.typ === 'bon') {
      const alt = this.letzterBon;
      const gleich =
        alt !== null &&
        alt.summeCent === n.summeCent &&
        alt.gutscheinCent === n.gutscheinCent &&
        JSON.stringify(alt.positionen) === JSON.stringify(n.positionen);
      if (!gleich) {
        this.letzteZahlung = null;
      }
      const bon = { ...n, geaendertUm: (gleich ? alt.geaendertUm : undefined) ?? this.jetzt() };
      this.letzterBon = bon;
      this.post(bon);
      return;
    }
    if (n.typ === 'zahlung') {
      this.letzteZahlung = n.status === 'keine' ? null : n;
    } else if (n.typ === 'bezahlt' || n.typ === 'leer') {
      this.letzteZahlung = null;
      if (n.typ === 'leer') {
        this.letzterBon = null;
      }
    }
    this.post(n);
  }

  private antworte(): void {
    // Nur ein Bon mit Positionen ist eine Antwort wert (leere Bons fragen sonst im Kreis).
    if (this.letzterBon === null || this.letzterBon.positionen.length === 0) {
      return;
    }
    this.post({ ...this.letzterBon, antwort: true });
    if (this.letzteZahlung !== null) {
      this.post(this.letzteZahlung);
    }
  }

  private post(n: KundenanzeigeNachricht): void {
    try {
      this.kanal?.postMessage({ ...n, quelle: this.quelle, kassenId: this.kassenId });
    } catch {
      // Kanal geschlossen (z. B. Fenster wird gerade abgebaut) – die Kasse arbeitet ohne Anzeige weiter.
    }
  }

  schliesse(): void {
    if (this.puls !== null) {
      this.umgebung.clearInterval?.(this.puls);
      this.puls = null;
    }
    this.post({ typ: 'ende' });
    if (this.kanal !== null) {
      this.kanal.onmessage = null;
      this.kanal.close();
    }
  }
}
